import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Trophy, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { getUserData, themesAPI, ThemeLeaderboardEntry } from "@/lib/api";
import { translateDomainName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface ThemeLeaderboardListProps {
  themeId: string;
  /** Only admins may pass an explicit category; regular users are locked server-side. */
  category?: "student" | "graduate";
  /** Show each row's category (useful when an admin views all categories merged together). */
  showCategoryBadge?: boolean;
}

export default function ThemeLeaderboardList({ themeId, category, showCategoryBadge = false }: ThemeLeaderboardListProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [viewer] = useState(() => getUserData());
  const [entries, setEntries] = useState<ThemeLeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!themeId) return;

    const loadLeaderboard = async () => {
      setIsLoading(true);
      try {
        const response = await themesAPI.getThemeLeaderboard(themeId, category);
        setEntries(response.data?.leaderboard || []);
      } catch (error: any) {
        toast({
          title: t("strengthsExplorer.leaderboardLoadFailed"),
          description: getLocalizedErrorMessage(error, "strengthsExplorer.leaderboardLoadFailedDesc"),
          variant: "destructive",
        });
        setEntries([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadLeaderboard();
  }, [themeId, category, t, toast]);

  const handleUserClick = (userId: string) => {
    const profilePath = viewer?.role === "admin"
      ? `/admin/users/${userId}/profile`
      : `/directory/${userId}/profile`;
    navigate(profilePath);
  };

  if (isLoading) {
    return (
      <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t("strengthsExplorer.loadingLeaderboard")}
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-2 p-8 text-center text-muted-foreground">
        <Users className="h-8 w-8 opacity-50" />
        {t("strengthsExplorer.leaderboardEmpty")}
      </Card>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" dir={isRtl ? "rtl" : "ltr"}>
      {entries.map((entry) => (
        <button
          key={entry.userId}
          type="button"
          onClick={() => handleUserClick(entry.userId)}
          className="relative flex min-h-[170px] w-full min-w-0 flex-col items-center rounded-lg border border-border bg-card p-4 text-center transition-colors hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          <Badge
            variant="outline"
            className={`absolute top-3 min-w-[52px] justify-center text-base ltr:left-3 rtl:right-3 ${
              entry.rank <= 3 ? "border-primary/40 font-bold text-primary" : ""
            }`}
          >
            #{entry.rank}
          </Badge>

          {entry.rank <= 3 && (
            <Trophy className="absolute top-4 h-5 w-5 text-primary ltr:right-4 rtl:left-4" />
          )}

          <Avatar className="h-16 w-16 shrink-0">
            <AvatarImage src={entry.profileImageDisplayUrl || entry.profileImage || undefined} alt={entry.name} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {entry.name?.charAt(0).toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>

          <div className="mt-3 min-w-0 w-full">
            <p className="truncate text-base font-semibold text-foreground">{entry.name}</p>
            {entry.topDomains && entry.topDomains.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center justify-center gap-1">
                {entry.topDomains.map(({ domain, count }) => {
                  const color = STRENGTHS_EXPLORER_DOMAIN_COLORS[domain];
                  return (
                    <Badge
                      key={domain}
                      className="border text-xs"
                      style={{ backgroundColor: `${color}15`, borderColor: `${color}40`, color }}
                    >
                      {translateDomainName(domain, t, i18n.language)} ({count})
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {showCategoryBadge && entry.category && (
            <Badge variant="secondary" className="mt-2 shrink-0 text-xs">
              {entry.category === "graduate" ? t("admin.categoryGraduate") : t("admin.categoryStudent")}
            </Badge>
          )}
        </button>
      ))}
    </div>
  );
}
